import React, { useState, useEffect, useMemo } from 'react';
import { PieChart } from 'lucide-react';
import { expenseApi } from '../services/api';
import { ExpenseMonthlyReport } from '../types';
import PageSuggestion from '../components/PageSuggestion';

const MONTH_LABELS = [
    '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const formatBRL = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((cents || 0) / 100);

const ExpenseReport: React.FC = () => {
    const now = new Date();
    const defaultYear = now.getFullYear();
    const defaultMonth = now.getMonth() + 1;

    const [year, setYear] = useState(defaultYear);
    const [month, setMonth] = useState(defaultMonth);
    const [report, setReport] = useState<ExpenseMonthlyReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const yearOptions = useMemo(
        () => Array.from({ length: 8 }, (_, i) => defaultYear - 3 + i),
        [defaultYear]
    );

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await expenseApi.getMonthlyReport({ year, month });
            setReport(res.data);
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { translatedMessage?: string; message?: string } } })?.response?.data
                    ?.translatedMessage ||
                (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Não foi possível carregar o relatório.';
            setError(typeof msg === 'string' ? msg : 'Erro ao carregar.');
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-secondary-900">Relatório mensal de gastos</h1>
                <p className="text-secondary-600">Totais por categoria no mês selecionado</p>
                <PageSuggestion>
                    Consulte este resumo no fechamento do mês para entender quais categorias mais consumiram orçamento.
                </PageSuggestion>
            </div>

            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-auto">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Ano</label>
                        <select
                            className="input-field min-w-[100px]"
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Mês</label>
                        <select
                            className="input-field min-w-[160px]"
                            value={month}
                            onChange={(e) => setMonth(Number(e.target.value))}
                        >
                            {MONTH_LABELS.slice(1).map((label, idx) => (
                                <option key={idx + 1} value={idx + 1}>{label}</option>
                            ))}
                        </select>
                    </div>
                    <button type="button" onClick={fetchReport} className="btn-primary w-full sm:w-auto h-[42px]" disabled={loading}>
                        {loading ? 'Carregando...' : 'Atualizar'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
            )}

            {report && !loading && (
                <>
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-medium text-primary-800">
                                Total em {MONTH_LABELS[report.month]} de {report.year}
                            </div>
                            <div className="mt-2 text-2xl sm:text-3xl font-bold text-primary-900 break-words">
                                {formatBRL(report.totalAmount)}
                            </div>
                        </div>
                        <PieChart className="h-12 w-12 text-primary-600" />
                    </div>

                    <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="min-w-[560px] w-full divide-y divide-secondary-200">
                            <thead className="bg-secondary-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase">
                                        Categoria
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase">
                                        Total
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase">
                                        % do mês
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary-200">
                                {report.categories.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-8 text-center text-secondary-600">
                                            Nenhum gasto neste mês.
                                        </td>
                                    </tr>
                                ) : (
                                    report.categories.map((row) => {
                                        const pct =
                                            report.totalAmount > 0
                                                ? Math.round((row.total / report.totalAmount) * 1000) / 10
                                                : 0;
                                        return (
                                            <tr key={row.categoryId} className="hover:bg-secondary-50">
                                                <td className="px-6 py-4 text-sm text-secondary-900">{row.categoryName}</td>
                                                <td className="px-6 py-4 text-sm text-right font-medium text-secondary-900">
                                                    {formatBRL(row.total)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-right text-secondary-600">{pct}%</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ExpenseReport;
